import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { location, programs, sites } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { SettingsCard, SettingsField } from '../settings/common.js';
import {
  EVV_SEPARATION_SENTENCE,
  NO_DELETE_MEANING,
  PROGRAM_ATTACHMENT_MEANING,
  WORK_SITES_ONLY_HELPER,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { RadiusPreview } from './RadiusPreview.js';
import {
  createBodyOf,
  draftOf,
  EMPTY_GEOFENCE_DRAFT,
  formatMeters,
  patchBodyOf,
  validateGeofenceDraft,
  type GeofenceDraft,
} from './geofenceForm.js';
import { messageOf } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Geofences — full CRUD over `work_geofence` through the real API:
 * `GET/POST /location/geofences`, `PATCH /location/geofences/:id`
 * (all `location:manage` — the LIST route itself carries that gate, which is
 * why this section is absent from the hub sub-nav for a read-only holder).
 *
 * Lifecycle honesty: the API has NO DELETE route, so there is no Delete
 * control here (directive §32 — a control that faked deletion by hiding a row
 * would be worse than its absence). Deactivating is the real retirement:
 * `listApplicableGeofences` only ever evaluates `status = 'active'` fences.
 *
 * The radius preview is an honest SVG — circle plus a real distance scale
 * (design doc §13). No map tiles, no external request of any kind.
 *
 * EVV separation (§12, load-bearing): work_geofence has no client_id, the
 * api-client types have no client field, and this page never invents one —
 * the wire-body tests pin its absence.
 */

interface Names {
  siteName: (id: string | null) => string | null;
  programName: (id: string | null) => string | null;
}

export function LocationGeofencesPage() {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['location', 'geofences'],
    queryFn: () => location.geofences(),
  });
  // Attachment vocabularies. Sites: the `location` entity (max page 200 —
  // listSitesSchema's own ceiling); programs: same options query the other
  // settings screens share.
  const siteOptions = useQuery({
    queryKey: ['sites', 'options'],
    queryFn: () => sites.list({ status: 'all', limit: 200 }),
  });
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200, sort: 'name' }),
  });

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<GeofenceDraft>(EMPTY_GEOFENCE_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<GeofenceDraft | null>(null);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const invalidate = () => {
    setMutateError(null);
    void qc.invalidateQueries({ queryKey: ['location', 'geofences'] });
    // A fence attached to a site shows on that site's coverage row too.
    void qc.invalidateQueries({ queryKey: ['sites'] });
  };

  const create = useMutation({
    mutationFn: (draft: GeofenceDraft) => location.createGeofence(createBodyOf(draft)),
    onSuccess: () => {
      invalidate();
      setCreating(false);
      setCreateDraft(EMPTY_GEOFENCE_DRAFT);
    },
    onError: (err: unknown) => setMutateError(messageOf(err)),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; patch: location.GeofencePatch }) =>
      location.updateGeofence(input.id, input.patch),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEditDraft(null);
    },
    onError: (err: unknown) => setMutateError(messageOf(err)),
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: 'active' | 'inactive' }) =>
      location.updateGeofence(input.id, { status: input.status }),
    onSuccess: invalidate,
    onError: (err: unknown) => setMutateError(messageOf(err)),
  });

  const names: Names = {
    siteName: (id) =>
      id == null ? null : (siteOptions.data?.sites.find((s) => s.id === id)?.name ?? null),
    programName: (id) =>
      id == null ? null : (programOptions.data?.programs.find((p) => p.id === id)?.name ?? null),
  };

  const geofences = list.data ?? [];
  const editing = editingId ? geofences.find((g) => g.id === editingId) : undefined;

  const columns: Column<location.Geofence>[] = [
    {
      key: 'name',
      header: 'Geofence',
      render: (g) => <span style={{ fontWeight: 600 }}>{g.name}</span>,
    },
    {
      key: 'center',
      header: 'Center',
      render: (g) => `${g.latitude.toFixed(5)}, ${g.longitude.toFixed(5)}`,
    },
    { key: 'radius', header: 'Radius', render: (g) => formatMeters(g.radiusMeters), numeric: true },
    {
      key: 'site',
      header: 'Work site',
      render: (g) =>
        g.locationId == null ? (
          <span className="rn-muted">—</span>
        ) : (
          (names.siteName(g.locationId) ?? <span className="rn-muted">Site not listed</span>)
        ),
    },
    {
      key: 'appliesTo',
      header: 'Applies to',
      render: (g) =>
        g.programId == null
          ? 'Every program'
          : (names.programName(g.programId) ?? 'One program (not listed)'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (g) => (
        <Pill variant={g.status === 'active' ? 'good' : 'plain'} dot>
          {g.status === 'active' ? 'Active' : 'Inactive'}
        </Pill>
      ),
    },
  ];

  return (
    <LocationSettingsShell active="geofences">
      <SettingsCard>
        <h2 className="rn-card-title">Geofences</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          The circles punches are checked against under the warn and require modes.{' '}
          {WORK_SITES_ONLY_HELPER}
        </p>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)', fontSize: 13 }}>
          {EVV_SEPARATION_SENTENCE}
        </p>

        {list.isLoading ? (
          <SkeletonSet surface="table" rows={3} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load geofences just now."
            onRetry={() => void list.refetch()}
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
            {geofences.length === 0 ? (
              <SuccessEmpty
                heading="No geofences yet"
                message="Add one below, or switch a geofence on for a site under Sites & locations."
              />
            ) : (
              <TableCard
                columns={columns}
                rows={geofences}
                rowKey={(g) => g.id}
                caption="Geofences: press Enter or Space on a row to edit it"
                selectedId={editingId ?? undefined}
                onRowOpen={(g) => {
                  setEditingId(g.id);
                  setEditDraft(draftOf(g));
                  setMutateError(null);
                }}
              />
            )}

            {mutateError ? (
              <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
                {mutateError}
              </p>
            ) : null}

            {!creating && !editing ? (
              <div>
                <button
                  type="button"
                  className="signin-devbtn"
                  style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                  onClick={() => {
                    setCreating(true);
                    setMutateError(null);
                  }}
                >
                  Add a geofence
                </button>
              </div>
            ) : null}
          </div>
        )}
      </SettingsCard>

      {creating && !editing ? (
        <SettingsCard>
          <h2 className="rn-card-title">New geofence</h2>
          <GeofenceForm
            draft={createDraft}
            set={(patch) => setCreateDraft((d) => ({ ...d, ...patch }))}
            names={names}
            siteChoices={siteOptions.data?.sites ?? []}
            programChoices={programOptions.data?.programs ?? []}
            busy={create.isPending}
            submitLabel={create.isPending ? 'Adding…' : 'Add geofence'}
            onSubmit={() => create.mutate(createDraft)}
            onCancel={() => {
              setCreating(false);
              setCreateDraft(EMPTY_GEOFENCE_DRAFT);
              setMutateError(null);
            }}
          />
        </SettingsCard>
      ) : null}

      {editing && editDraft ? (
        <SettingsCard>
          <h2 className="rn-card-title">Edit “{editing.name}”</h2>
          <GeofenceForm
            draft={editDraft}
            set={(patch) => setEditDraft((d) => (d ? { ...d, ...patch } : d))}
            names={names}
            siteChoices={siteOptions.data?.sites ?? []}
            programChoices={programOptions.data?.programs ?? []}
            busy={update.isPending}
            submitLabel={update.isPending ? 'Saving…' : 'Save geofence'}
            onSubmit={() => {
              const patch = patchBodyOf(editing, editDraft);
              if (!patch) {
                // Nothing changed — the server would refuse an empty patch.
                setEditingId(null);
                setEditDraft(null);
                return;
              }
              update.mutate({ id: editing.id, patch });
            }}
            onCancel={() => {
              setEditingId(null);
              setEditDraft(null);
              setMutateError(null);
            }}
          />
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-8)' }}>
            <p className="rn-muted" style={{ fontSize: 13 }}>
              {NO_DELETE_MEANING}
            </p>
            <div>
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({
                    id: editing.id,
                    status: editing.status === 'active' ? 'inactive' : 'active',
                  })
                }
              >
                {setStatus.isPending
                  ? 'Working…'
                  : editing.status === 'active'
                    ? 'Deactivate this geofence'
                    : 'Reactivate this geofence'}
              </button>
            </div>
          </div>
        </SettingsCard>
      ) : null}
    </LocationSettingsShell>
  );
}

/**
 * One form for create and edit — the same fields, the same validation, the
 * same wire bounds. NO client field exists here, and none may be added
 * (EVV separation; the tests pin the wire body).
 */
function GeofenceForm({
  draft,
  set,
  names,
  siteChoices,
  programChoices,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  draft: GeofenceDraft;
  set: (patch: Partial<GeofenceDraft>) => void;
  names: Names;
  siteChoices: sites.Site[];
  programChoices: programs.Program[];
  busy: boolean;
  submitLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const errors = validateGeofenceDraft(draft);
  const valid = Object.keys(errors).length === 0;
  const radius = errors.radiusMeters == null ? Number(draft.radiusMeters.trim()) : null;

  const chosenSite = draft.locationId
    ? siteChoices.find((s) => s.id === draft.locationId)
    : undefined;
  const siteHasPosition = chosenSite?.latitude != null && chosenSite?.longitude != null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !busy) onSubmit();
      }}
      style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}
    >
      <SettingsField label="Name" error={errors.name}>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => set({ name: e.target.value })}
          maxLength={200}
          style={{ maxWidth: 320 }}
        />
      </SettingsField>

      <div style={{ display: 'flex', gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <SettingsField label="Latitude" error={errors.latitude}>
          <input
            type="text"
            inputMode="decimal"
            value={draft.latitude}
            onChange={(e) => set({ latitude: e.target.value })}
            style={{ maxWidth: 140 }}
          />
        </SettingsField>
        <SettingsField label="Longitude" error={errors.longitude}>
          <input
            type="text"
            inputMode="decimal"
            value={draft.longitude}
            onChange={(e) => set({ longitude: e.target.value })}
            style={{ maxWidth: 140 }}
          />
        </SettingsField>
        <SettingsField
          label="Radius in meters"
          hint="Between 1 m and 50 km (50,000 m)."
          error={errors.radiusMeters}
        >
          <input
            type="text"
            inputMode="numeric"
            value={draft.radiusMeters}
            onChange={(e) => set({ radiusMeters: e.target.value })}
            style={{ maxWidth: 120 }}
          />
        </SettingsField>
      </div>

      <RadiusPreview radiusMeters={radius} />

      <SettingsField
        label="Work site"
        hint="Optional: record which site this fence belongs to. Attachment does not change which punches are checked."
      >
        <select
          value={draft.locationId}
          onChange={(e) => set({ locationId: e.target.value })}
          style={{ maxWidth: 320 }}
        >
          <option value="">No site</option>
          {siteChoices
            .filter((s) => s.status !== 'retired' || s.id === draft.locationId)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          {draft.locationId && names.siteName(draft.locationId) == null ? (
            <option value={draft.locationId}>Site not listed</option>
          ) : null}
        </select>
      </SettingsField>

      {siteHasPosition ? (
        <div>
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() =>
              set({
                latitude: String(chosenSite!.latitude),
                longitude: String(chosenSite!.longitude),
              })
            }
          >
            Use {chosenSite!.name}’s map position as the center
          </button>
        </div>
      ) : null}

      <SettingsField label="Applies to" hint={PROGRAM_ATTACHMENT_MEANING}>
        <select
          value={draft.programId}
          onChange={(e) => set({ programId: e.target.value })}
          style={{ maxWidth: 320 }}
        >
          <option value="">Every program</option>
          {programChoices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          {draft.programId && names.programName(draft.programId) == null ? (
            <option value={draft.programId}>Program not listed</option>
          ) : null}
        </select>
      </SettingsField>

      <div style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
        <button
          type="submit"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          disabled={!valid || busy}
        >
          {submitLabel}
        </button>
        <button type="button" className="rn-btn-quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
